import React from 'react';
import dayjs from 'dayjs';
import {
  PackageTimelineEvent,
  PackageTimelineRider,
} from '@/services/api/packages';
import { getPackageStatusLabel } from './PackageStatusBadge';

// The handover-scan actor is a rider: show `CODE - name` instead of the raw
// rider id. Falls back gracefully when the snapshot lacked a code or name.
const riderLabel = (rider: PackageTimelineRider): string =>
  [rider.code, rider.name].filter(Boolean).join(' - ') || `#${rider.id}`;

const fmt = (v: string) => dayjs(v).format('DD MMM YYYY, HH:mm');

// Terminal item states (Status Model v2 §1) — the flow stops here. Note the
// renames: DELIVERED → COMPLETED, RETURNED → RETURNED_TO_HUB.
const SETTLED_STATUSES = [
  'COMPLETED',
  'RETURNED_TO_HUB',
  'RETURNED_TO_CLIENT',
];

// The newest (last) event carries the item's current status — its dot gets
// the terminal/active tone; every earlier node already happened.
const dotClass = (status: string, isLast: boolean): string => {
  if (!isLast) return 'bg-success-base';
  if (status === 'CANCELLED' || status === 'LOST') return 'bg-error-base';
  if (SETTLED_STATUSES.includes(status)) return 'bg-success-base';
  return 'bg-primary';
};

interface PackageTimelineProps {
  events: PackageTimelineEvent[];
}

// Maps the item's status-history (oldest-first) onto a simple vertical
// timeline: chronological top-to-bottom, last node reflects current status.
const PackageTimeline: React.FC<PackageTimelineProps> = ({ events }) => {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-stroke-soft-200 py-8 text-text-soft-400">
        <i className="ri-history-line text-2xl" />
        <span className="text-sm">Belum ada riwayat status</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {events.map((e, i) => {
        const isLast = i === events.length - 1;
        return (
          <div key={e.id} className="relative pb-4 pl-5 last:pb-0">
            {!isLast && (
              <span
                className="absolute left-[3px] top-3 bottom-0 w-px bg-stroke-soft-200"
                aria-hidden="true"
              />
            )}
            <span
              className={`absolute left-0 top-1.5 h-[7px] w-[7px] rounded-full ${dotClass(
                e.toStatus,
                isLast
              )}`}
              aria-hidden="true"
            />
            <div className="text-[13px] font-semibold text-text-strong-950">
              {getPackageStatusLabel(e.toStatus)}
            </div>
            <div className="mt-0.5 text-[11px] text-text-soft-400">
              {fmt(e.changedAt)}
            </div>
            {e.note && (
              <div className="mt-1 text-[12px] text-text-sub-600">
                {e.note}
              </div>
            )}
            {(e.rider || e.changedBy) && (
              <div className="mt-0.5 text-[11px] text-text-soft-400">
                oleh {e.rider ? riderLabel(e.rider) : e.changedBy}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PackageTimeline;
